'use client'

// React imports
import React, { createContext, useContext, useMemo, useState } from 'react'

// Local utility and hook imports
import { useTicket } from './TicketContext'
import { useOvertimeBet } from '@/hooks/useOvertimeBet'
import useLiveSportsMarketsQuery from '@/Overtime/queries/useLiveSportsMarketsQuery'
import useChainProvider from '@/hooks/useChainProvider'
import { SYSTEM_BET_MINIMUM_MARKETS } from '@/Overtime/constants/markets'
import type { TicketPosition } from '@/Overtime/types/markets'
import type { SupportedNetwork } from '@/Overtime/types/network'

export enum OvertimeBetslipDisableReason {
  ConditionState = 'ConditionState',
  BetAmountGreaterThanMaxBet = 'BetAmountGreaterThanMaxBet',
  BetAmountLowerThanMinBet = 'BetAmountLowerThanMinBet',
  ComboWithForbiddenItem = 'ComboWithForbiddenItem',
  ComboWithSameGame = 'ComboWithSameGame',
  PrematchConditionInStartedGame = 'PrematchConditionInStartedGame',
  FreeBetWithLive = 'FreeBetWithLive',
  FreeBetWithCombo = 'FreeBetWithCombo',
  FreeBetExpired = 'FreeBetExpired',
  FreeBetMinOdds = 'FreeBetMinOdds',
}

type DetailedOvertimeBetslipContextValue = {
  betAmount: string
  odds: Record<string, number>
  totalOdds: number
  maxBet: number | undefined
  minBet: number | undefined
  states: Record<string, string>
  disableReason: OvertimeBetslipDisableReason | undefined
  changeBetAmount: (value: string) => void
  isStatesFetching: boolean
  isOddsFetching: boolean
  isMaxBetFetching: boolean
  isBetAllowed: boolean
  ticket: TicketPosition[]
  payout: number
  isFetching: boolean
  fetchTicketAmmQuote: (
    buyInAmountForQuote: number,
    fetchQuoteOnly: boolean
  ) => Promise<any>
  payment: any
  setPaymentAmountToBuy: (amount: number | string) => void
  setPaymentSelectedCollateralIndex: (payload: {
    selectedCollateralIndex: number
    networkId: number
    forcedChange?: boolean
  }) => void
}

const DetailedOvertimeBetslipContext = createContext<
  DetailedOvertimeBetslipContextValue | undefined
>(undefined)

export const DetailedOvertimeBetslipProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const {
    ticket,
    state,
    setPaymentAmountToBuy,
    setPaymentSelectedCollateralIndex,
  } = useTicket()
  const [betAmount, setBetAmount] = useState('')
  const [isStatesFetching, setIsStatesFetching] = useState(false)

  const { isOvertimeChain, chainId } = useChainProvider()

  // Construct networkConfig for Overtime queries
  const networkConfig = useMemo(
    () => ({ networkId: chainId as SupportedNetwork }),
    [chainId]
  )

  // Fetch live Overtime odds
  const { data: liveMarketsData, isFetching: isOddsFetching } =
    useLiveSportsMarketsQuery(true, networkConfig)

  // Build odds and states objects for the current ticket
  const { odds, states } = useMemo(() => {
    const oddsObj: Record<string, number> = {}
    const statesObj: Record<string, string> = {}

    ticket.forEach((item, index) => {
      // Try to find a live market
      const liveMarket = liveMarketsData?.live?.find(
        m => m.gameId === item.gameId
      )

      const now = Date.now() / 1000 // current time in seconds

      // Use maturityDate (ISO string) if available and is a string, otherwise fallback to maturity (number)
      let maturity: number | undefined = undefined
      if (
        liveMarket?.maturityDate &&
        typeof liveMarket.maturityDate === 'string'
      ) {
        maturity = Date.parse(liveMarket.maturityDate) / 1000
      } else if (typeof liveMarket?.maturity === 'number') {
        maturity = liveMarket.maturity
      }

      if (liveMarket && liveMarket.live) {
        // Found in live markets - use live data
        oddsObj[item.gameId] = liveMarket.odds[item.position]
        // If market is live and open, skip maturity check
        const isActive =
          liveMarket.isOpen &&
          !liveMarket.isResolved &&
          !liveMarket.isCancelled &&
          (liveMarket.live ? true : maturity !== undefined && now <= maturity)
        // Debug log for live market
        console.log('[Betslip] LiveMarket check', {
          gameId: item.gameId,
          isOpen: liveMarket.isOpen,
          isResolved: liveMarket.isResolved,
          isCancelled: liveMarket.isCancelled,
          maturity,
          now,
          'now <= maturity':
            maturity !== undefined ? now <= maturity : 'maturity undefined',
          isActive,
          odds: liveMarket.odds,
          position: item.position,
          oddsForPosition: liveMarket.odds[item.position],
        })
        statesObj[item.gameId] = isActive ? 'active' : 'settled'
      } else {
        // Only mark as active if the market is actually available and open
        if (
          item.market &&
          item.market.odds &&
          item.market.odds[item.position]
        ) {
          // Use maturityDate (ISO string) if available and is a string, otherwise fallback to maturity (number)
          let fallbackMaturity: number | undefined = undefined
          if (
            item.market.maturityDate &&
            typeof item.market.maturityDate === 'string'
          ) {
            fallbackMaturity = Date.parse(item.market.maturityDate) / 1000
          } else if (typeof item.market.maturity === 'number') {
            fallbackMaturity = item.market.maturity
          }
          oddsObj[item.gameId] = item.market.odds[item.position]
          const isActive =
            item.market.isOpen &&
            !item.market.isResolved &&
            !item.market.isCancelled &&
            (Boolean((item.market as any).live)
              ? true
              : fallbackMaturity !== undefined && now <= fallbackMaturity)
          // Debug log for fallback market
          console.log('[Betslip] FallbackMarket check', {
            gameId: item.gameId,
            isOpen: item.market.isOpen,
            isResolved: item.market.isResolved,
            isCancelled: item.market.isCancelled,
            fallbackMaturity,
            now,
            'now <= fallbackMaturity':
              fallbackMaturity !== undefined
                ? now <= fallbackMaturity
                : 'maturity undefined',
            isActive,
            odds: item.market.odds,
            position: item.position,
            oddsForPosition: item.market.odds[item.position],
          })
          statesObj[item.gameId] = isActive ? 'active' : 'settled'
        } else {
          // Market not available - mark as settled/inactive
          statesObj[item.gameId] = 'settled'
        }
      }
    })

    return { odds: oddsObj, states: statesObj }
  }, [liveMarketsData, ticket])

  const isAmountBiggerThanMinBet =
    Boolean(betAmount) && typeof SYSTEM_BET_MINIMUM_MARKETS !== 'undefined'
      ? +betAmount >= SYSTEM_BET_MINIMUM_MARKETS
      : true

  // Only allow bet if all items are active
  const areAllItemsActive = ticket.every(
    item => states[item.gameId] === 'active'
  )

  const isBetAllowed = isAmountBiggerThanMinBet && areAllItemsActive

  // Always call useOvertimeBet - the hook handles chain support internally
  const { maxBet, isMaxBetFetching, payout, isFetching, fetchTicketAmmQuote } =
    useOvertimeBet({
      ticket,
      betAmount,
      selectedCollateralIndex: isOvertimeChain
        ? state.payment.selectedCollateralIndex
        : 0,
      systemBetDenominator: 2,
    })

  const value = useMemo(
    () => ({
      betAmount,
      odds, // <-- use the live odds here!
      maxBet,
      minBet: SYSTEM_BET_MINIMUM_MARKETS,
      totalOdds: 0,
      states, // <-- provide states here
      disableReason: undefined,
      changeBetAmount: (value: string) => setBetAmount(value),
      isStatesFetching,
      isOddsFetching, // <-- use the fetching state from the hook
      isMaxBetFetching,
      isBetAllowed,
      ticket: ticket,
      payout,
      isFetching,
      fetchTicketAmmQuote,
      payment: state.payment,
      setPaymentAmountToBuy: setPaymentAmountToBuy,
      setPaymentSelectedCollateralIndex: setPaymentSelectedCollateralIndex,
    }),
    [
      betAmount,
      odds, // <-- add odds as a dependency
      states, // <-- add states as a dependency
      isStatesFetching,
      isOddsFetching,
      isMaxBetFetching,
      ticket,
      isBetAllowed,
      maxBet,
      payout,
      isFetching,
      fetchTicketAmmQuote,
      state.payment,
      setPaymentAmountToBuy,
      setPaymentSelectedCollateralIndex,
    ]
  )

  return (
    <DetailedOvertimeBetslipContext.Provider value={value}>
      {children}
    </DetailedOvertimeBetslipContext.Provider>
  )
}

export const useDetailedOvertimeBetslip = () => {
  const context = useContext(DetailedOvertimeBetslipContext)
  if (context === undefined) {
    throw new Error(
      'useDetailedOvertimeBetslip must be used within a DetailedOvertimeBetslipProvider'
    )
  }
  return context
}
